import React, { SetStateAction, useEffect, useMemo, useRef } from 'react';

import { useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatISO } from 'date-fns';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { StorageLocationAutoComplete } from '@/components';
import { CalendarWrapper as Calendar } from '@/components/common/calendar-wrapper';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { STRING_MAP_SEARCH } from '@/types.gql';

import { useCreateSemen } from '../../../services/semen-pull/hooks';
import { CreateSemen, createSemenSchema } from '../schema';
import { useSemenPullContext } from '../useSemenPullContext';

const SEX_OBJECT_TYPE_CODE = 10029;
const SEXED_OBJECT_TYPE_CODE = 10029;
const CONVENTIONAL_SEMEN_TYPE = 'Conv';

const normalizeSemenTypeName = (semenType?: string | null) =>
  semenType === 'Unknown' || semenType === 'Conventional'
    ? CONVENTIONAL_SEMEN_TYPE
    : semenType;

const getScheduledSemenTypeName = (
  semenTypeName?: string | null,
  semenSexed?: string | null,
  semenSex?: string | null,
  useConventionalType = false,
) => {
  if (!useConventionalType) return semenTypeName;

  const normalizedSemenTypeName = normalizeSemenTypeName(semenTypeName);
  if (normalizedSemenTypeName) return normalizedSemenTypeName;

  if (semenSexed === 'No' && semenSex === 'Unknown') {
    return CONVENTIONAL_SEMEN_TYPE;
  }

  return normalizedSemenTypeName;
};

type Props = {
  createSemenDialogVisible: boolean;
  setCreateSemenDialogVisible: React.Dispatch<SetStateAction<boolean>>;
};

export const CreateSemenDialog = ({
  createSemenDialogVisible,
  setCreateSemenDialogVisible,
}: Props) => {
  const { t } = useTranslation();
  const { currentScheduledSemen, refetchPulledSemen, refetchScheduledSemen } =
    useSemenPullContext();
  const [params] = useSearchParams();
  const selectedSiteId = params.get('site');
  const selectedDate = params.get('date');
  const calendarRef = useRef<Calendar>(null);

  const createSemenMutation = useCreateSemen();

  const isSemenPullRestEnabled = useFeatureFlag(
    '29181_semen_pull_graphql_to_rest',
  );
  const isSemenConventionalTypeEnabled = useFeatureFlag(
    'Semen_Conventional_Type',
  );
  const scheduledSemenTypeName = getScheduledSemenTypeName(
    currentScheduledSemen?.semenTypeName,
    currentScheduledSemen?.semenSexed,
    currentScheduledSemen?.semenSex,
    isSemenConventionalTypeEnabled,
  );

  const { data: sexesData, loading: sexesLoading } = useQuery(
    STRING_MAP_SEARCH,
    {
      variables: { objectTypeCode: SEX_OBJECT_TYPE_CODE, name: 'tog_sex' },
      skip: !createSemenDialogVisible,
    },
  );

  const { data: sexedData, loading: sexedLoading } = useQuery(
    STRING_MAP_SEARCH,
    {
      variables: { objectTypeCode: SEXED_OBJECT_TYPE_CODE, name: 'tog_sexed' },
      skip: !createSemenDialogVisible,
    },
  );

  const { data: cssCertifiedData, loading: cssCertifiedLoading } = useQuery(
    STRING_MAP_SEARCH,
    {
      variables: {
        objectTypeCode: SEXED_OBJECT_TYPE_CODE,
        name: 'tog_csscertified',
      },
      skip: !createSemenDialogVisible,
    },
  );

  const sexes = useMemo(
    () =>
      sexesData?.stringMap?.map((item) => ({
        label:
          isSemenConventionalTypeEnabled &&
          (item.key.toString() === '3' || item.value === 'Unknown')
            ? 'Conventional'
            : item.value,
        value: item.key.toString(),
      })),
    [isSemenConventionalTypeEnabled, sexesData],
  );

  const sexed = useMemo(
    () =>
      sexedData?.stringMap?.map((item) => ({
        label: item.value,
        value: item.key.toString(),
      })),
    [sexedData],
  );

  const cssCertified = useMemo(
    () =>
      cssCertifiedData?.stringMap?.map((item) => ({
        label: item.value,
        value: item.key.toString(),
      })),
    [cssCertifiedData],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createSemenSchema),
    defaultValues: {
      semenType: scheduledSemenTypeName,
      fertSiteId: selectedSiteId,
      donorScheduleDate: selectedDate,
      sireId: currentScheduledSemen?.sireId,
      accountId: currentScheduledSemen?.accountId,
      donorScheduleSemenLocation: currentScheduledSemen?.semenStorageId,
      donorScheduleSemenType: scheduledSemenTypeName,
      cane: currentScheduledSemen?.cane,
      colCode: currentScheduledSemen?.colCode,
      naab: currentScheduledSemen?.naab,
      recDate: new Date(),
      available: currentScheduledSemen?.quantity,
      move: currentScheduledSemen?.moveNum || 0,
      comment: '',
      sexed: '9999',
      sex: '9999',
      storage: { id: '', name: '' },
      cssCertified: '9999',
      secondaryOwner: currentScheduledSemen?.secondaryOwner ?? '',
    } as CreateSemen,
  });

  useEffect(() => {
    if (!currentScheduledSemen) return;

    const derivedSexed = (() => {
      switch (scheduledSemenTypeName) {
        case 'Conv':
        case '.RS F':
        case '.RS M':
          return '0';
        case 'SF M':
        case 'SF F':
          return '1';
        default:
          return '0';
      }
    })();

    const derivedSex = (() => {
      switch (scheduledSemenTypeName) {
        case 'Conv':
        case '.RS F':
        case '.RS M':
          return '3';
        case 'SF M':
          return '2';
        case 'SF F':
          return '1';
        default:
          return '3';
      }
    })();

    reset({
      semenType: scheduledSemenTypeName,
      fertSiteId: selectedSiteId,
      donorScheduleDate: selectedDate,
      sireId: currentScheduledSemen.sireId,
      accountId: currentScheduledSemen.accountId,
      donorScheduleSemenLocation: currentScheduledSemen.semenStorageId,
      donorScheduleSemenType: scheduledSemenTypeName,
      cane: currentScheduledSemen.cane?.toString(),
      colCode: currentScheduledSemen.colCode?.toString(),
      naab: currentScheduledSemen.naab,
      recDate: new Date(),
      available: currentScheduledSemen.quantity,
      move: currentScheduledSemen.moveNum,
      comment: '',
      sexed:
        currentScheduledSemen.semenSexed && sexed
          ? (sexed.find(
              (item) => item.value === currentScheduledSemen.semenSexed,
            )?.value ?? derivedSexed)
          : derivedSexed,
      sex:
        currentScheduledSemen.semenSex && sexes
          ? (sexes.find((item) => item.value === currentScheduledSemen.semenSex)
              ?.value ?? derivedSex)
          : derivedSex,
      storage: { id: '', name: '' },
      cssCertified: '9999',
      secondaryOwner: currentScheduledSemen.secondaryOwner ?? '',
    } as CreateSemen);
  }, [
    currentScheduledSemen,
    reset,
    selectedDate,
    selectedSiteId,
    scheduledSemenTypeName,
    sexes,
    sexed,
  ]);

  const formValues = watch();
  const createSemenDisabled = !(
    formValues.sexed !== '9999' &&
    formValues.sex !== '9999' &&
    formValues.cssCertified !== '9999' &&
    formValues.storage?.id !== '' &&
    formValues.cane &&
    formValues.colCode &&
    formValues.naab &&
    formValues.recDate
  );

  const handleCreateSemen = async (data: CreateSemen) => {
    const { storage = {}, ...newSemenInput } = {
      ...data,
      semenType: data.semenType as string,
      fertSiteId: data.fertSiteId as string,
      sireId: data.sireId as string,
      donorScheduleDate: formatISO(new Date(selectedDate as string), {
        representation: 'date',
      }),
      accountId: data.accountId as string,
      donorScheduleSemenLocation:
        currentScheduledSemen?.semenStorageId as string,
      donorScheduleSemenType: scheduledSemenTypeName as string,
      cane: data.cane as string,
      colCode: data.colCode as string,
      naab: data.naab as string,
      recDate: formatISO(data.recDate),
      available: data.available as number,
      move: data.move as number,
      comment: data.comment as string,
      sexed: parseInt(data.sexed),
      sex: parseInt(data.sex),
      storageId: data.storage.id,
      cssCertified: parseInt(data.cssCertified),
      secondaryOwner: data.secondaryOwner ?? null,
    };

    try {
      await createSemenMutation.mutateAsync(newSemenInput);
      refetchScheduledSemen();
      refetchPulledSemen();
    } catch (error) {
      console.error('Error creating semen:', error);
      throw error;
    }
  };

  return (
    <Dialog
      header={t('semen.newSemen')}
      visible={createSemenDialogVisible}
      onHide={() => {
        if (!createSemenDialogVisible) return;
        setCreateSemenDialogVisible(false);
      }}
      style={{ width: '50vw', minWidth: '520px' }}
      maximizable
    >
      <form
        onSubmit={handleSubmit(handleCreateSemen)}
        className="grid grid-cols-2 gap-x-4 gap-y-4"
      >
        <div className="flex flex-col gap-1">
          <label htmlFor="">Cane Code:</label>
          <Controller
            name="cane"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
          {errors.cane && (
            <div className="text-sm text-red-500">{errors.cane.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Col Code:</label>
          <Controller
            name="colCode"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
          {errors.colCode && (
            <div className="text-sm text-red-500">{errors.colCode.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">NAAB:</label>
          <Controller
            name="naab"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Date Received:</label>
          <Controller
            name="recDate"
            control={control}
            render={({ field }) => (
              <Calendar
                value={field.value ? new Date(field.value) : undefined}
                dateFormat="mm-dd-yy"
                showIcon
                onChange={(e) => field.onChange(e.value)}
                onSelect={(e) => {
                  field.onChange(e.value);
                  const currentField = calendarRef.current
                    ?.getElement()
                    ?.closest('.flex.flex-col.gap-1');
                  const nextInput =
                    currentField?.nextElementSibling?.querySelector(
                      'input, button',
                    );
                  if (nextInput instanceof HTMLElement) {
                    nextInput.focus();
                  }
                }}
                ref={calendarRef}
              />
            )}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Available:</label>
          <Controller
            name="available"
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputNumber value={value} onChange={(e) => onChange(e.value)} />
            )}
          />
          {errors.available && (
            <div className="text-sm text-red-500">
              {errors.available.message}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Move:</label>
          <Controller
            name="move"
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputNumber
                value={value ?? 0}
                onChange={(e) => onChange(e.value)}
              />
            )}
          />
          {errors.move && (
            <div className="text-sm text-red-500">{errors.move.message}</div>
          )}
        </div>
        <div className="col-span-2 flex flex-col gap-1">
          <label htmlFor="">Comment:</label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => <InputText {...field} />}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Sexed:</label>
          <Controller
            name="sexed"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={sexed}
                loading={sexedLoading}
                onChange={(e) => {
                  field.onChange(e.value);
                }}
                showClear
              />
            )}
          />
          {errors.sexed && (
            <div className="text-sm text-red-500">{errors.sexed.message}</div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="">Sex:</label>
          <Controller
            name="sex"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={sexes}
                onChange={(e) => {
                  field.onChange(e.value);
                }}
                loading={sexesLoading}
              />
            )}
          />
        </div>
        <div className="col-span-2 flex flex-col gap-1">
          <label htmlFor="">Storage Location:</label>
          <Controller
            name="storage"
            control={control}
            render={({ field }) => (
              <StorageLocationAutoComplete
                label={null}
                value={field.value}
                appendTo={null}
                onSelect={(e) => field.onChange(e.value)}
              />
            )}
          />
        </div>
        {isSemenPullRestEnabled && (
          <div className="flex flex-col gap-1">
            <label htmlFor="">{t('semen.secondaryOwner')}</label>
            <Controller
              name="secondaryOwner"
              control={control}
              render={({ field }) => (
                <InputText {...field} value={field.value ?? ''} />
              )}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <label htmlFor="">CSS Stamped:</label>
          <Controller
            name="cssCertified"
            control={control}
            render={({ field }) => (
              <Dropdown
                {...field}
                appendTo="self"
                options={cssCertified}
                onChange={(e) => {
                  field.onChange(e.value);
                }}
                loading={cssCertifiedLoading}
              />
            )}
          />
        </div>
        <div className="col-span-2 flex justify-end gap-4 pt-2">
          <Button
            label={t('semen.newSemen')}
            severity="success"
            type="submit"
            loading={createSemenMutation.isPending}
            disabled={createSemenDisabled}
          />
          <Button
            label={t('common.cancel')}
            severity="danger"
            onClick={() => {
              setCreateSemenDialogVisible(false);
            }}
          />
        </div>
      </form>
    </Dialog>
  );
};
